import type { Occupancy } from '@elara/domain/models/schemas/room-schemas';

import type { ToolBadgeSpec } from '../types.js';

import { formatOccupancy } from '#lib/occupancy-utils.js';
import { pluralize } from '#lib/utils.js';

import { readArray, readField, readString } from '../extractors.js';

interface RoomSearchToolOutput {
  hotel?: { name?: string | null };
}

/** Substitutes HOTEL_NAME placeholder from the suspended workflow's hotel field. */
function hotelNameFrom(output: unknown): string {
  const raw = output as RoomSearchToolOutput | undefined;
  return raw?.hotel?.name ?? 'a hotel';
}

/** Reads an Occupancy from any object — used for both tool args and per-rate occupancy. */
function readOccupancy(source: unknown): Occupancy | undefined {
  if (!source || typeof source !== 'object') return undefined;
  const adults = readField(source, 'adults');
  if (typeof adults !== 'number') return undefined;
  const children = readField(source, 'children');
  const childrenAges = readArray(readField(source, 'childrenAges'));
  return {
    adults,
    ...(typeof children === 'number' ? { children } : {}),
    ...(childrenAges ? { childrenAges: childrenAges.filter((a) => typeof a === 'number') as number[] } : {}),
  };
}

/** Reads the occupancy from the tool input args (added in the single-occupancy migration). */
function occupancyFromArgs(args: unknown): Occupancy | undefined {
  return readOccupancy(readField(args, 'occupancy'));
}

/**
 * Reads the occupancy the search was run for. Sourced from the resume tool's
 * top-level `occupancy` output, which the workflow surfaces from its init data
 * (the original fetchRoomAvailability call), or from `request.occupancy` on the
 * single-call fetchTravelportRooms output. Reading from per-rate `occupancy`
 * is unreliable because rates from multiple searches against the same hotel
 * can be merged into one room option.
 */
function occupancyFromRoomsOutput(output: unknown): Occupancy | undefined {
  return (
    readOccupancy(readField(output, 'occupancy')) ?? readOccupancy(readField(readField(output, 'request'), 'occupancy'))
  );
}

/** "Searching for rooms at X for 2 adults" — shared by every room-search tool's plain label. */
function roomSearchLabel(toolCall: Parameters<ToolBadgeSpec['label']>[0], active: boolean): string {
  const hotelName = hotelNameFrom(toolCall.output);
  const occupancy = occupancyFromArgs(toolCall.request?.input);
  const occupancyLabel = occupancy ? ` for ${formatOccupancy(occupancy)}` : '';
  return active
    ? `Searching for rooms at ${hotelName}${occupancyLabel}`
    : `Searched for rooms at ${hotelName}${occupancyLabel}`;
}

/**
 * fetchRoomAvailability — suspends the room-search workflow with a hotel + room list.
 * Renders as a plain label; results are suppressed because resumeRoomSearchWorkflow
 * (which usually follows immediately) supersedes it.
 */
export const fetchRoomAvailabilitySpec: ToolBadgeSpec = {
  toolName: 'fetchRoomAvailability',
  icon: 'hotel',
  label: roomSearchLabel,
  activitySummary: { key: 'roomSearch', label: (_, active) => (active ? 'Searching for rooms' : 'Searched for rooms') },
};

/**
 * Builds the expandable room list (thumbnails + bed/occupancy subtitles) from a tool
 * output carrying `rooms[]` of persisted room options and a `hotel.name`.
 */
function extractRoomResults(toolCall: Parameters<NonNullable<ToolBadgeSpec['extractResults']>>[0]) {
  const output = toolCall.output;
  const arr = readArray(readField(output, 'rooms'));
  if (!arr) return undefined;
  const hotelName = readString(readField(readField(output, 'hotel'), 'name'));
  const items = [];
  for (const [index, entry] of arr.entries()) {
    const id = readString(readField(entry, 'id'));
    if (!id) continue;
    const title =
      readString(readField(readField(entry, 'generated'), 'name')) ??
      readString(readField(entry, 'displayName')) ??
      readString(readField(entry, 'type')) ??
      readString(readField(entry, 'name')) ??
      `Result ${index + 1}`;
    const bedTypes = readArray(readField(entry, 'bedTypes')) ?? [];
    const bedParts = bedTypes
      .map((bt) => {
        const count = readField(bt, 'count');
        const description = readString(readField(bt, 'description'));
        if (typeof count === 'number' && count > 0 && description) return `${count} ${pluralize(count, description)}`;
        if (description) return description;
        return undefined;
      })
      .filter((p): p is string => !!p);
    const bedsLabel = bedParts.length > 0 ? bedParts.join(', ') : undefined;
    const maxOccupancy = readField(entry, 'maxOccupancy');
    const sleepsLabel = typeof maxOccupancy === 'number' && maxOccupancy > 0 ? `Sleeps ${maxOccupancy}` : undefined;
    const subtitle = [bedsLabel, sleepsLabel].filter(Boolean).join(' · ') || undefined;
    const firstImage = readArray(readField(entry, 'images'))?.[0];
    const iconUrl = readString(readField(firstImage, 'url'));
    items.push({
      key: `${id}-${index}`,
      title,
      subtitle,
      previewType: 'room' as const,
      id,
      iconUrl,
      iconVariant: iconUrl ? ('thumbnail' as const) : undefined,
    });
  }
  const occupancy = occupancyFromRoomsOutput(output);
  const occupancyLabel = occupancy ? ` for ${formatOccupancy(occupancy)}` : '';
  const contextLabel = hotelName
    ? `at ${hotelName}${occupancyLabel}`
    : occupancyLabel
      ? occupancyLabel.trimStart()
      : undefined;
  return { items, contextLabel };
}

/**
 * fetchTravelportRooms — the single-call Travelport search that searches AND curates, so
 * there is no resume step to supersede it: the one badge carries both the in-progress
 * label and the expandable room list.
 */
export const fetchTravelportRoomsSpec: ToolBadgeSpec = {
  toolName: 'fetchTravelportRooms',
  icon: 'hotel',
  label: roomSearchLabel,
  activitySummary: { key: 'roomSearch', label: (_, active) => (active ? 'Searching for rooms' : 'Searched for rooms') },
  resultNoun: { singular: 'room', plural: 'rooms' },
  dataSource: 'the hotel',
  extractResults: extractRoomResults,
};

/**
 * resumeRoomSearchWorkflow — returns enriched rooms after the concierge's selection.
 * Produces the expandable room list (with thumbnails + bed/occupancy subtitles).
 */
export const resumeRoomSearchWorkflowSpec: ToolBadgeSpec = {
  toolName: 'resumeRoomSearchWorkflow',
  icon: 'hotel',
  label: (_, active) => (active ? 'Getting room rates' : 'Fetched room rates'),
  activitySummary: { key: 'roomSearch', label: (_, active) => (active ? 'Searching for rooms' : 'Searched for rooms') },
  resultNoun: { singular: 'room', plural: 'rooms' },
  dataSource: 'the hotel',
  extractResults: extractRoomResults,
};
